import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { loadError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { MailOpen, Save, Building2, BellRing, ImagePlus, Trash2, Image } from "lucide-react";
import { useRef } from "react";
import { applyAccent, applyFontPair, FONT_PAIRS } from "@/lib/theme";

const ACCENT_PRESETS = [
  { name: "Smeraldo", hex: "#1f8a5b" }, { name: "Terracotta", hex: "#c86d51" }, { name: "Oro", hex: "#d4af37" },
  { name: "Blu oceano", hex: "#1565c0" }, { name: "Vinaccia", hex: "#8e244d" }, { name: "Ardesia", hex: "#546e7a" }, { name: "Viola", hex: "#6a4fb3" },
];

const DEFAULT_LOGIN_IMAGE = "https://images.unsplash.com/photo-1533554030380-20991a0f9c9e?crop=entropy&cs=srgb&fm=jpg&q=85";
import { toast } from "sonner";

const Field = ({ label, testId, value, onChange, placeholder, type = "text" }) => (
  <div>
    <Label className="text-xs uppercase tracking-wider">{label}</Label>
    <Input data-testid={testId} type={type} className="mt-1.5" value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} />
  </div>
);

const Section = ({ icon: Icon, title, desc, children }) => (
  <Card className="p-6 border-border shadow-sm animate-fade-up">
    <div className="flex items-center gap-3 mb-5">
      <div className="w-11 h-11 rounded-xl bg-secondary grid place-items-center"><Icon className="w-5 h-5 text-accent" /></div>
      <div>
        <h3 className="font-heading text-lg font-semibold">{title}</h3>
        <p className="text-sm text-muted-foreground">{desc}</p>
      </div>
    </div>
    {children}
  </Card>
);

export default function Settings() {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadingHero, setUploadingHero] = useState(false);
  const fileRef = useRef(null);
  const heroRef = useRef(null);
  const set = (k) => (v) => setS((f) => ({ ...f, [k]: v }));

  const uploadHero = async (file) => {
    if (!file) return;
    setUploadingHero(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/settings/login-image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setS((f) => ({ ...f, login_image_url: data.login_image_url }));
      toast.success("Immagine di accesso aggiornata");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setUploadingHero(false); if (heroRef.current) heroRef.current.value = ""; }
  };

  const removeHero = async () => {
    try {
      const { data } = await api.delete("/settings/login-image");
      setS((f) => ({ ...f, login_image_url: data.login_image_url }));
      toast.success("Ripristinata l'immagine predefinita");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const uploadLogo = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/settings/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setS((f) => ({ ...f, logo_url: data.logo_url }));
      toast.success("Logo caricato");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const removeLogo = async () => {
    try {
      const { data } = await api.delete("/settings/logo");
      setS((f) => ({ ...f, logo_url: data.logo_url }));
      toast.success("Logo rimosso");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const [uploadingDark, setUploadingDark] = useState(false);
  const darkFileRef = useRef(null);
  const uploadDarkLogo = async (file) => {
    if (!file) return;
    setUploadingDark(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/settings/logo?variant=dark", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setS((f) => ({ ...f, logo_dark_url: data.logo_dark_url }));
      toast.success("Logo per tema scuro caricato");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setUploadingDark(false); if (darkFileRef.current) darkFileRef.current.value = ""; }
  };
  const removeDarkLogo = async () => {
    try {
      const { data } = await api.delete("/settings/logo?variant=dark");
      setS((f) => ({ ...f, logo_dark_url: data.logo_dark_url }));
      toast.success("Logo per tema scuro rimosso");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  useEffect(() => { api.get("/settings").then((r) => setS(r.data)).catch(loadError); }, []);
  const pickAccent = (hex) => { setS((f) => ({ ...f, accent_color: hex })); applyAccent(hex); };
  const pickFont = (key) => { setS((f) => ({ ...f, font_pair: key })); applyFontPair(key); };

  const save = async () => {
    if (!s.structure_name?.trim()) { toast.error("Il nome della struttura è obbligatorio"); return; }
    setSaving(true);
    try {
      const { data } = await api.put("/settings", s);
      setS(data);
      toast.success("Impostazioni salvate");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  if (!s) return null;

  return (
    <div className="space-y-6" data-testid="settings-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-accent font-semibold">Configurazione</p>
          <h1 className="font-display text-4xl sm:text-5xl mt-1">Impostazioni</h1>
        </div>
        <Button data-testid="save-settings-button" onClick={save} disabled={saving} className="bg-accent hover:bg-accent/90 text-accent-foreground rounded-full h-11 px-5 font-heading">
          <Save className="w-4 h-4 mr-2" />{saving ? "Salvataggio…" : "Salva impostazioni"}
        </Button>
      </div>

      <div className="grid lg:grid-cols-2 gap-5 max-w-5xl">
        <Section icon={Building2} title="Dati struttura" desc="Usati su ricevute, fatture ed email inviate agli ospiti.">
          <div className="space-y-4">
            <div>
              <Label className="text-xs uppercase tracking-wider">Logo</Label>
              <div className="flex items-center gap-4 mt-1.5">
                <div className="w-20 h-20 rounded-xl border border-border bg-secondary/40 grid place-items-center overflow-hidden shrink-0">
                  {s.logo_url ? <img src={s.logo_url} alt="Logo" data-testid="logo-preview" className="w-full h-full object-contain" /> : <ImagePlus className="w-6 h-6 text-muted-foreground" />}
                </div>
                <div className="space-y-2">
                  <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" data-testid="logo-file-input" onChange={(e) => uploadLogo(e.target.files?.[0])} />
                  <div className="flex gap-2">
                    <Button type="button" variant="outline" size="sm" data-testid="upload-logo-button" disabled={uploading} onClick={() => fileRef.current?.click()}>
                      <ImagePlus className="w-4 h-4 mr-1.5" />{uploading ? "Caricamento…" : s.logo_url ? "Sostituisci" : "Carica logo"}
                    </Button>
                    {s.logo_url && <Button type="button" variant="ghost" size="sm" data-testid="remove-logo-button" onClick={removeLogo} className="text-destructive"><Trash2 className="w-4 h-4" /></Button>}
                  </div>
                  <p className="text-xs text-muted-foreground">PNG, JPG o WebP · max 2 MB · mostrato su ricevute, fatture ed email</p>
                </div>
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider">Logo per tema scuro <span className="normal-case tracking-normal text-muted-foreground">(opzionale)</span></Label>
              <div className="flex items-center gap-4 mt-1.5">
                <div className="w-20 h-20 rounded-xl border border-border grid place-items-center overflow-hidden shrink-0" style={{ background: "hsl(160 20% 5%)" }}>
                  {s.logo_dark_url ? <img src={s.logo_dark_url} alt="Logo scuro" data-testid="logo-dark-preview" className="w-full h-full object-contain" /> : s.logo_url ? <img src={s.logo_url} alt="" className="w-full h-full object-contain opacity-60" /> : <ImagePlus className="w-6 h-6 text-stone-400" />}
                </div>
                <div className="space-y-2">
                  <input ref={darkFileRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" data-testid="logo-dark-file-input" onChange={(e) => uploadDarkLogo(e.target.files?.[0])} />
                  <div className="flex gap-2">
                    <Button type="button" variant="outline" size="sm" data-testid="upload-logo-dark-button" disabled={uploadingDark} onClick={() => darkFileRef.current?.click()}>
                      <ImagePlus className="w-4 h-4 mr-1.5" />{uploadingDark ? "Caricamento…" : s.logo_dark_url ? "Sostituisci" : "Carica logo scuro"}
                    </Button>
                    {s.logo_dark_url && <Button type="button" variant="ghost" size="sm" data-testid="remove-logo-dark-button" onClick={removeDarkLogo} className="text-destructive"><Trash2 className="w-4 h-4" /></Button>}
                  </div>
                  <p className="text-xs text-muted-foreground">Usato nella sidebar e nel login quando il tema scuro è attivo. Se assente, viene usato il logo principale.</p>
                </div>
              </div>
            </div>
            <Field label="Nome struttura" testId="structure-name-input" value={s.structure_name} onChange={set("structure_name")} placeholder="Residenza Bella Vista" />
            <Field label="P.IVA / Codice Fiscale" testId="structure-vat-input" value={s.structure_vat} onChange={set("structure_vat")} placeholder="IT01234567890" />
            <Field label="Indirizzo" testId="structure-address-input" value={s.structure_address} onChange={set("structure_address")} placeholder="Via Roma 12, 80067 Sorrento (NA)" />
            <Field label="Email struttura (risposte ospiti)" testId="structure-email-input" type="email" value={s.structure_email} onChange={set("structure_email")} placeholder="info@struttura.it" />
            <div className="border-t border-border pt-4 mt-2">
              <p className="text-xs uppercase tracking-wider text-accent font-semibold mb-3">Numerazione documenti</p>
              <div className="grid grid-cols-3 gap-3">
                <Field label="Prefisso fatture" testId="invoice-prefix-input" value={s.invoice_prefix} onChange={set("invoice_prefix")} placeholder="FT" />
                <Field label="Prefisso ricevute" testId="receipt-prefix-input" value={s.receipt_prefix} onChange={set("receipt_prefix")} placeholder="RIC" />
                <Field label="Prefisso note di credito" testId="credit-note-prefix-input" value={s.credit_note_prefix} onChange={set("credit_note_prefix")} placeholder="NC" />
              </div>
              <p className="text-xs text-muted-foreground mt-2">Contatori annuali separati, es. <b>{(s.invoice_prefix || "").toUpperCase() || "—"}-0001/{new Date().getFullYear()}</b> e <b>{(s.receipt_prefix || "").toUpperCase() || "—"}-0001/{new Date().getFullYear()}</b>. I documenti già emessi mantengono il numero attuale.</p>
            </div>
            <div className="border-t border-border pt-4 mt-2">
              <p className="text-xs uppercase tracking-wider text-accent font-semibold mb-3">Colore accento</p>
              <div className="flex flex-wrap items-center gap-2" data-testid="accent-presets">
                {ACCENT_PRESETS.map((c) => (
                  <button key={c.hex} type="button" title={c.name} data-testid={`accent-preset-${c.hex.slice(1)}`} onClick={() => pickAccent(c.hex)}
                    className={`w-8 h-8 rounded-full border-2 transition-transform hover:scale-110 ${(s.accent_color || "").toLowerCase() === c.hex ? "border-foreground scale-110" : "border-transparent"}`} style={{ background: c.hex }} />
                ))}
                <label className="flex items-center gap-2 text-xs text-muted-foreground ml-1 cursor-pointer">
                  <input type="color" data-testid="accent-color-input" value={s.accent_color || "#1f8a5b"} onChange={(e) => pickAccent(e.target.value)} className="w-8 h-8 rounded-full border border-border bg-transparent cursor-pointer" />
                  Personalizzato {s.accent_color && <span className="font-mono">{s.accent_color}</span>}
                </label>
                {s.accent_color && <Button type="button" variant="ghost" size="sm" data-testid="accent-reset-button" onClick={() => pickAccent("")} className="h-8 text-xs">Ripristina predefinito</Button>}
              </div>
              <p className="text-xs text-muted-foreground mt-2">Colore di pulsanti, link e titoli di sezione per tutti gli utenti. L'anteprima è immediata; ricorda di salvare.</p>
            </div>
            <div className="border-t border-border pt-4 mt-2">
              <p className="text-xs uppercase tracking-wider text-accent font-semibold mb-3">Caratteri</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="font-pairs">
                {Object.entries(FONT_PAIRS).map(([key, p]) => {
                  const active = (s.font_pair || "classic") === key;
                  return (
                    <button key={key} type="button" data-testid={`font-pair-${key}`} onClick={() => pickFont(key)} onMouseEnter={() => { if (!document.getElementById(`gf-${key}`) && key !== "classic") { const l = document.createElement("link"); l.id = `gf-${key}`; l.rel = "stylesheet"; l.href = `https://fonts.googleapis.com/css2?${p.gf}&display=swap`; document.head.appendChild(l); } }}
                      className={`text-left rounded-lg border px-3 py-2.5 transition-colors ${active ? "border-accent bg-accent/10" : "border-border hover:bg-secondary/60"}`}>
                      <div className="flex items-baseline justify-between gap-2">
                        <span className="text-xl leading-none" style={{ fontFamily: p.display }}>Residenza</span>
                        <span className="text-sm font-semibold" style={{ fontFamily: p.heading }}>{p.name}</span>
                      </div>
                      <div className="text-xs text-muted-foreground mt-1" style={{ fontFamily: p.body }}>Prenotazioni, incassi e ricevute · {p.desc}</div>
                    </button>
                  );
                })}
              </div>
              <p className="text-xs text-muted-foreground mt-2">Coppia di caratteri per titoli e testo, valida per tutti gli utenti. Anteprima immediata; ricorda di salvare.</p>
            </div>
            <div className="border-t border-border pt-4 mt-2">
              <p className="text-xs uppercase tracking-wider text-accent font-semibold mb-3">Mensa — orari standard</p>
              <div className="grid grid-cols-4 gap-3">
                <Field label="Inizio colazione" testId="breakfast-start-input" type="time" value={s.breakfast_start || "07:30"} onChange={set("breakfast_start")} />
                <Field label="Fine colazione" testId="breakfast-end-input" type="time" value={s.breakfast_end || "10:00"} onChange={set("breakfast_end")} />
                <Field label="Ora pranzo" testId="lunch-time-input" type="time" value={s.lunch_time || "12:30"} onChange={set("lunch_time")} />
                <Field label="Ora cena" testId="dinner-time-input" type="time" value={s.dinner_time || "20:00"} onChange={set("dinner_time")} />
              </div>
              <p className="text-xs text-muted-foreground mt-2">Orari predefiniti per la pagina Mensa; possono essere cambiati per singolo giorno.</p>
            </div>
            <div className="border-t border-border pt-4 mt-2">
              <p className="text-xs uppercase tracking-wider text-accent font-semibold mb-3">Fedeltà</p>
              <Field label="Sconto ospiti abituali (%) — 0 per disattivare" testId="loyalty-discount-input" type="number" value={String(s.loyalty_discount_pct ?? "")} onChange={set("loyalty_discount_pct")} placeholder="10" />
              <p className="text-xs text-muted-foreground mt-2">Nel form prenotazione verrà suggerito lo sconto per gli ospiti con almeno 2 soggiorni.</p>
            </div>
          </div>
        </Section>

        <div className="space-y-5">
          <Section icon={Image} title="Immagine pagina di accesso" desc="Foto mostrata a sinistra nella schermata di login (consigliato formato verticale, min 1200px).">
            <div className="rounded-xl overflow-hidden border border-border bg-secondary/40 h-40 relative">
              <img src={s.login_image_url || DEFAULT_LOGIN_IMAGE} alt="Anteprima login" data-testid="login-image-preview" className="w-full h-full object-cover" />
              {!s.login_image_url && <span className="absolute bottom-2 left-2 text-[10px] uppercase tracking-wider bg-black/50 text-white px-2 py-0.5 rounded-full">Predefinita</span>}
            </div>
            <input ref={heroRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" data-testid="login-image-file-input" onChange={(e) => uploadHero(e.target.files?.[0])} />
            <div className="flex gap-2 mt-3">
              <Button type="button" variant="outline" size="sm" data-testid="upload-login-image-button" disabled={uploadingHero} onClick={() => heroRef.current?.click()}>
                <ImagePlus className="w-4 h-4 mr-1.5" />{uploadingHero ? "Caricamento…" : s.login_image_url ? "Sostituisci immagine" : "Carica immagine"}
              </Button>
              {s.login_image_url && <Button type="button" variant="ghost" size="sm" data-testid="remove-login-image-button" onClick={removeHero} className="text-destructive"><Trash2 className="w-4 h-4 mr-1.5" />Ripristina predefinita</Button>}
            </div>
            <p className="text-xs text-muted-foreground mt-2">PNG, JPG o WebP · max 5 MB</p>
            <div className="mt-4 space-y-3 border-t border-border pt-4">
              <Field label="Sottotitolo (sopra il nome)" testId="login-tagline-input" value={s.login_tagline} onChange={set("login_tagline")} placeholder="Gestionale Affittacamere" />
              <div>
                <Label className="text-xs uppercase tracking-wider">Frase di benvenuto</Label>
                <Textarea data-testid="login-welcome-input" className="mt-1.5" rows={2} value={s.login_welcome || ""} onChange={(e) => set("login_welcome")(e.target.value)}
                  placeholder="Camere, prenotazioni, incassi e ricevute della tua struttura, in un unico posto elegante." />
              </div>
            </div>
          </Section>

          <Section icon={BellRing} title="Avviso arrivo allo staff" desc="Quando un ospite indica l'orario di arrivo, riceverai un'email a questo indirizzo.">
            <Field label="Email di notifica" testId="staff-notify-email-input" type="email" value={s.staff_notify_email} onChange={set("staff_notify_email")} placeholder="reception@struttura.it" />
            <p className="text-xs text-muted-foreground mt-2">Lascia vuoto per disattivare le notifiche.</p>
          </Section>

          <Section icon={MailOpen} title="Messaggio di benvenuto" desc="Aggiunto in fondo all'email di promemoria del giorno prima dell'arrivo.">
            <Textarea data-testid="welcome-message-input" rows={5} value={s.reminder_welcome_message || ""} onChange={(e) => set("reminder_welcome_message")(e.target.value)}
              placeholder={"Es. Al suo arrivo troverà le chiavi nella cassetta accanto al portone. Per qualsiasi necessità: +39 333 0000000."} />
            <p className="text-xs text-muted-foreground mt-2">{(s.reminder_welcome_message || "").length} caratteri · vuoto per non aggiungere nulla</p>
          </Section>
        </div>
      </div>
    </div>
  );
}
